import { useEffect, useState, useRef, useMemo } from "react";
import axiosInstance from "../../../api/axiosInstance";
import { getTeethNos, nl2br } from "../../../utils/functions";
import DentalDiagram from "./DentalDiagram";
import ViewServiceModal from "./ViewServiceModal";
import moment from 'moment'
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { triggerLabRefresh } from "../../../store/labSlice";
import Select from "react-select";
import MedicalTemplateList from "../SubComponents/MedicalTemplateList";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { baseurImage } from "../../../Baseurl";
import logo1 from "../../../assests/logo.png";

export default function Dental(props) {
	const { dateSelectData = [], onAddMedicalNote, dataAllergy = [], chronicillness1 = [], onOpenAllergy, onDeleteAllergy, onOpenChronicIllness, onEditChronicIllness, onDeleteChronicIllness } = props;
	const navigate = useNavigate();
	const [contextMenuData, setContextMenuData] = useState({
		visible: false,
		text: "-"
	});
	const [isFdi, setIsFdi] = useState(false);
	const [currentDentalDate, setCurrentDentalDate] = useState("");
	const [selectedServiceDetails, setSelectedServiceDetails] = useState({});
	const [viewServiceModalOpen, setViewServiceModalOpen] = useState(false);
	const [dentalDates, setDentalDates] = useState([]);
	const [patientServices, setPatientServices] = useState([]);
	const dispatch = useDispatch();
	const allBranchSettings = useSelector(state => state.business.allBranchSettings);
	const diagramRef = useRef(null)
	const diagramContainerRef = useRef(null)

	// ── Prescription state ──
	const EMPTY_MED = { rxId: null, medicine_name: "", dose: "", frequency: "", duration: "", notes: "", dosage: "", form: "", substance: "", productType: "", packet_size: "", route: "", strength: "", unit: "" };
	const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [editingGroupId, setEditingGroupId] = useState(null);
	const [editIndex, setEditIndex] = useState(null);
	const [rxList, setRxList] = useState([]);
	const [prescriptions, setPrescriptions] = useState([]);
	const [medicineData, setMedicineData] = useState({ ...EMPTY_MED });
	const [addedMedicines, setAddedMedicines] = useState([]);

	useEffect(() => {
		axiosInstance.get("getAllRxList").then(res => {
			if (res.data.success) setRxList(res.data.data);
		}).catch(console.error);
	}, []);

	const fetchPrescriptions = async () => {
		try {
			const res = await axiosInstance.get(`getPatientDiseaseDetails/${props.patientId}`);
			if (res.data.success) setPrescriptions(res.data.data?.prescriptions || []);
		} catch (e) { console.error(e); }
	};

	useEffect(() => { fetchPrescriptions(); }, []);

	// ── Medical Notes panel ──
	const [notesModalOpen, setNotesModalOpen] = useState(false);
	const [selectedNoteDate, setSelectedNoteDate] = useState(null);
	const [selectedNoteData, setSelectedNoteData] = useState(null);
	const [notesLoading, setNotesLoading] = useState(false);

	// ── Teeth Notes panel ──
	const [teethNotes, setTeethNotes] = useState([]);
	const [teethNotesLoading, setTeethNotesLoading] = useState(false);
	const [viewingTeethNote, setViewingTeethNote] = useState(null);
	const [isEditingTeethNote, setIsEditingTeethNote] = useState(false);
	const [editTeethNoteText, setEditTeethNoteText] = useState("");
	const [teethNoteSaving, setTeethNoteSaving] = useState(false);
	const [recordLockHours, setRecordLockHours] = useState(null);

	useEffect(() => {
		axiosInstance.get("getBusinessSettings")
			.then((res) => {
				const h = res.data?.data?.record_lock_hours;
				if (h != null) setRecordLockHours(Number(h));
			})
			.catch(() => {});
	}, []);

	const isRecordLocked = (timestamp) => {
		if (recordLockHours == null || !timestamp) return false;
		return (Date.now() - new Date(timestamp).getTime()) > recordLockHours * 3600 * 1000;
	};

	const loadTeethNotes = async () => {
		setTeethNotesLoading(true);
		try {
			const res = await axiosInstance.get(`getTeethNotes/${props.patientId}`);
			if (res.data?.success) setTeethNotes(res.data.data);
		} catch (e) {
			console.error(e);
		} finally {
			setTeethNotesLoading(false);
		}
	};

	const handleStartEditTeethNote = () => {
		setEditTeethNoteText(viewingTeethNote?.note_text || "");
		setIsEditingTeethNote(true);
	};

	const handleCancelEditTeethNote = () => {
		setIsEditingTeethNote(false);
		setEditTeethNoteText("");
	};

	const handleSaveTeethNote = async () => {
		if (!editTeethNoteText.trim() || !viewingTeethNote) return;
		setTeethNoteSaving(true);
		try {
			const res = await axiosInstance.put(`updateTeethNote/${viewingTeethNote.id}`, { note_text: editTeethNoteText.trim() });
			if (res.data?.success) {
				setViewingTeethNote((prev) => prev ? { ...prev, note_text: editTeethNoteText.trim() } : prev);
				setIsEditingTeethNote(false);
				await loadTeethNotes();
			}
		} catch (e) {
			console.error(e);
			Swal.fire("Error", "Failed to update note.", "error");
		} finally {
			setTeethNoteSaving(false);
		}
	};

	const handleDeleteTeethNote = async () => {
		if (!viewingTeethNote) return;
		const confirm = await Swal.fire({
			title: "Delete this note?",
			text: "This action cannot be undone.",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Delete",
			confirmButtonColor: "#d33",
		});
		if (!confirm.isConfirmed) return;
		try {
			const res = await axiosInstance.delete(`deleteTeethNote/${viewingTeethNote.id}`);
			if (res.data?.success) {
				setViewingTeethNote(null);
				setIsEditingTeethNote(false);
				await loadTeethNotes();
			}
		} catch (e) {
			console.error(e);
			Swal.fire("Error", "Failed to delete note.", "error");
		}
	};

	const handleNoteClick = async (item) => {
		const date = moment(item.appointmentDate, "YYYY-MM-DDTHH:mm:ss").format("YYYY-MM-DD");
		setSelectedNoteDate({ date, doctor_name: item.doctor_name });
		setNotesLoading(true);
		setNotesModalOpen(true);
		try {
			const res = await axiosInstance.post(`getMedicalRecordsByDate/${props.patientId}`, { date });
			if (res.status === 200) setSelectedNoteData(res.data.data);
			else setSelectedNoteData(null);
		} catch (e) {
			console.error(e);
			setSelectedNoteData(null);
		} finally {
			setNotesLoading(false);
		}
	};

	const handlemodaldpresctiption = () => {
		setIsEditMode(false);
		setEditingGroupId(null);
		setAddedMedicines([]);
		setMedicineData({ ...EMPTY_MED });
		setPrescriptionModalOpen(true);
	};

	const handleEditPrescription = (plan) => {
		if (!plan?.group_id) { Swal.fire("Error", "Prescription Id missing", "error"); return; }
		setIsEditMode(true);
		setEditingGroupId(plan.group_id);
		setAddedMedicines((plan.medicines || []).map(med => ({
			rxId: med.rxId || null, medicine_name: med.medicine_name, dose: med.dose || "",
			frequency: med.frequency || "", duration: med.duration || "", notes: med.notes || "",
			dosage: med.dosage || "", form: med.form || "", substance: med.substance || "",
			productType: med.productType || "", packet_size: med.packet_size || "",
			route: med.route || "", strength: med.strength || "", unit: med.unit || "",
		})));
		setPrescriptionModalOpen(true);
	};

	const rxapihit = async (rxId) => {
		try {
			const res = await axiosInstance.get(`getRxById/${rxId}`);
			if (res.data.success) {
				const d = res.data.data;
				setMedicineData(prev => ({
					...prev, medicine_name: d.medicine_name || "", dose: d.dose || "",
					frequency: d.frequency || "", duration: d.duration || "", notes: d.notes || "",
					substance: d.activeSubstances || "", packet_size: d.packet_size || "",
					form: d.form || "", productType: d.productType || "", route: d.route || "",
					strength: d.strength || "", unit: d.unit || "",
				}));
			}
		} catch (e) { console.error(e); }
	};

	const handleEditLabChange123 = (selectedOption) => {
		if (!selectedOption) return;
		setMedicineData(prev => ({ ...prev, rxId: selectedOption.value }));
		rxapihit(selectedOption.value);
	};

	const handleMedicineChange = (e) => {
		const { name, value } = e.target;
		setMedicineData(prev => ({ ...prev, [name]: value }));
	};

	const handleAddMedicine = async () => {
		if (!medicineData.medicine_name) { Swal.fire("Warning", "Please select a medicine", "warning"); return; }
		let proceed = true;
		try {
			if (medicineData.rxId) {
				const res = await axiosInstance.post("prescriptionAllergyCheckSingle", { patient_id: props.patientId, prescriptionId: medicineData.rxId });
				if (res.data.success && res.data.allergy_found === 1) {
					const r = await Swal.fire({ icon: "warning", title: "Warning", text: res.data.message || "Allergy conflict detected. Continue?", showCancelButton: true, confirmButtonText: "Yes", cancelButtonText: "No" });
					if (!r.isConfirmed) proceed = false;
				}
			}
		} catch (e) { console.error(e); }
		if (!proceed) return;
		if (editIndex !== null) {
			const updated = [...addedMedicines];
			updated[editIndex] = { ...medicineData };
			setAddedMedicines(updated);
			setEditIndex(null);
		} else {
			setAddedMedicines(prev => [...prev, { ...medicineData }]);
		}
		setMedicineData({ ...EMPTY_MED });
	};

	const handleEditMedicine = (index) => { setMedicineData(addedMedicines[index]); setEditIndex(index); };
	const handleDeleteMedicine = (index) => { setAddedMedicines(prev => prev.filter((_, i) => i !== index)); };

	const handleSelectPerscriptionTemplate = async (template) => {
		template.rxList.forEach(item => {
			item.rxId = item.id;
			axiosInstance.post("prescriptionAllergyCheckSingle", { patient_id: props.patientId, prescriptionId: item.id })
				.then(async res => {
					if (res.data.success && res.data.allergy_found === 1) {
						const r = await Swal.fire({ icon: "warning", title: "Warning", text: `Allergy conflict for ${item.medicine_name}. Continue?`, showCancelButton: true, confirmButtonText: "Yes", cancelButtonText: "No" });
						if (r.isConfirmed) setAddedMedicines(prev => [...prev, { ...item }]);
					} else if (res.data.success) {
						item.substance = item.active_substances;
						item.form = item.pharmaceutical_form;
						item.productType = item.product_type;
						setAddedMedicines(prev => [...prev, { ...item }]);
					} else {
						const r = await Swal.fire({ icon: "warning", title: "Warning", text: `Could not check allergies for ${item.medicine_name}. Continue?`, showCancelButton: true, confirmButtonText: "Yes", cancelButtonText: "No" });
						if (r.isConfirmed) setAddedMedicines(prev => [...prev, { ...item }]);
					}
				}).catch(console.error);
		});
	};

	const handlesubmitprescription = async () => {
		if (!addedMedicines.length) { Swal.fire("Warning", "Please add at least one medicine", "warning"); return; }
		try {
			let response;
			if (isEditMode) {
				response = await axiosInstance.put(`updatePrescriptionGroup/${editingGroupId}`, {
					patient_id: props.patientId,
					medicines: addedMedicines.map(med => ({
						medicine_name: med.medicine_name, strength: med.strength || null,
						frequency: med.frequency || null, dose: med.dose || null,
						dosage: med.dosage || null, product_type: med.product_type || null,
						duration: med.duration || null, route: med.route || null,
						substance: med.substance || null, packet_size: med.packet_size || null,
						notes: med.notes || null,
					})),
				});
			} else {
				const doctorId = (() => { try { return JSON.parse(localStorage.getItem("userMedical"))?.data?.id; } catch { return null; } })();
				response = await axiosInstance.post("addPrescription", { doctor_id: doctorId, patient_id: props.patientId, prescriptions: addedMedicines, branch_id: parseInt(localStorage.getItem('activeBranchId')) || (allBranchSettings.length === 1 ? allBranchSettings[0]?.id : null) });
			}
			if (response.data.success) {
				setPrescriptionModalOpen(false);
				setIsEditMode(false);
				setEditingGroupId(null);
				setAddedMedicines([]);
				setMedicineData({ ...EMPTY_MED });
				fetchPrescriptions();
				Swal.fire("Success", isEditMode ? "Prescription updated successfully" : "Prescription added successfully", "success");
			} else {
				Swal.fire("Error", response.data.message || "Operation failed", "error");
			}
		} catch (e) {
			console.error(e);
			Swal.fire("Error", e?.response?.data?.message || "Something went wrong", "error");
		}
	};

	const hadlenavigate = (id) => { navigate("/Admin/Prescription", { state: { id } }); };

	const hadlenavigatedelete = async (groupId) => {
		const result = await Swal.fire({ title: "Are you sure?", text: "You will not be able to recover this Prescription!", icon: "warning", showCancelButton: true, confirmButtonColor: "#3085d6", cancelButtonColor: "#d33", confirmButtonText: "Yes, delete it!" });
		if (result.isConfirmed) {
			try {
				const res = await axiosInstance.delete(`deletePrescription/${groupId}`);
				if (res.data.success) { Swal.fire("Deleted!", "Prescription has been deleted.", "success"); fetchPrescriptions(); }
				else Swal.fire("Error", "Failed to delete Prescription", "error");
			} catch (e) { Swal.fire("Error", "Something went wrong!", "error"); }
		}
	};

	const handlePrintPlan = async () => {
		let diagramImg = "";
		const chartEl = diagramContainerRef.current?.querySelector('.teethDiagramContainer');
		if (chartEl) {
			try {
				const canvas = await html2canvas(chartEl, { scale: 1.5, useCORS: true, logging: false });
				diagramImg = canvas.toDataURL("image/png");
			} catch (e) { console.error("Diagram capture failed", e); }
		}

		const bs = JSON.parse(localStorage.getItem("businessSetting") || "{}");
		const logoUrl = bs?.image ? `${baseurImage}${bs.image}` : logo1;
		const pd = props.patientDetails || {};
		const patientName = `${pd.firstName || ""} ${pd.lastName || ""}`.trim() || "N/A";
		const civilId = pd.civilIdNumber || pd.cpr || "N/A";
		const phone = pd.mobileNumber || pd.phone || "N/A";
		const gender = pd.gender || "N/A";
		const age = patientAge ?? "N/A";

		const planServices = patientServices.filter(p => {
			const s = (p.status || "").toLowerCase();
			return s !== "complete" && s !== "completed";
		});

		const tableRows = planServices.map((p, i) => `
			<tr>
				<td>${i + 1}</td>
				<td>${p.service_name || ""}</td>
				<td>${getTeethNos(p.details, isFdi).join(", ")}</td>
				<td>${p.extra_notes || ""}</td>
				<td>${p.amount || ""} BHD</td>
			</tr>`).join("");

		const pw = window.open("", "_blank");
		pw.document.open();
		pw.document.write(`<!DOCTYPE html>
<html><head><title>Dental Plan - ${patientName}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 10mm; background: #fff; }
  .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 14px; }
  .header img { height: 70px; margin-bottom: 6px; }
  .header h2 { margin: 4px 0; font-size: 18px; }
  .header p { margin: 2px 0; font-size: 12px; }
  .patient-box { display: flex; justify-content: space-between; border: 1px solid #ccc; border-radius: 4px; padding: 10px 16px; margin-bottom: 16px; font-size: 13px; }
  .patient-box p { margin: 3px 0; }
  .diagram { text-align: center; margin: 16px 0; }
  .diagram img { max-width: 100%; border: 1px solid #eee; }
  h3 { font-size: 14px; margin: 14px 0 6px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #999; padding: 5px 8px; font-size: 12px; text-align: left; }
  th { background: #f0f0f0; font-weight: bold; }
  @page { size: A4; margin: 10mm; }
</style></head><body>
<div class="header">
  <img src="${logoUrl}" onerror="this.style.display='none'" />
  <h2>${bs?.official_name || ""}</h2>
  <p>${bs?.address || ""}</p>
  <p>${bs?.telephone_1 ? "Tel: " + bs.telephone_1 : ""}${bs?.email ? "  |  Email: " + bs.email : ""}</p>
  ${bs?.cr_number ? `<p>CR: ${bs.cr_number}${bs?.vat_number ? "  |  VAT: " + bs.vat_number : ""}</p>` : ""}
</div>
<div class="patient-box">
  <div>
    <p><strong>Patient:</strong> ${patientName}</p>
    <p><strong>Civil ID:</strong> ${civilId}</p>
    <p><strong>Phone:</strong> ${phone}</p>
  </div>
  <div>
    <p><strong>Gender:</strong> ${gender}</p>
    <p><strong>Age:</strong> ${age}</p>
    <p><strong>Date:</strong> ${new Date().toLocaleDateString("en-GB")}</p>
  </div>
</div>
<h3>Dental Chart</h3>
<div class="diagram">
  ${diagramImg ? `<img src="${diagramImg}" />` : "<p>Diagram unavailable</p>"}
</div>
<h3>Treatment Plan</h3>
<table>
  <thead><tr><th>#</th><th>Service</th><th>Teeth</th><th>Notes</th><th>Amount</th></tr></thead>
  <tbody>${tableRows || '<tr><td colspan="5" style="text-align:center">No plan procedures found</td></tr>'}</tbody>
</table>
<script>window.onload = function() { window.focus(); window.print(); };<\/script>
</body></html>`);
		pw.document.close();
	};

	const teethTypeDescription = {
		"side": "Side",
		"root": "Root",
		"number": "Tooth",
		"surface": "Surface",
	}

	// Age in whole years, or null when unknown. Drives which dental chart the
	// diagram opens on, and the printed plan header.
	const patientAge = useMemo(() => {
		const pd = props.patientDetails || {};
		if (pd.age !== undefined && pd.age !== null && pd.age !== "" && !isNaN(Number(pd.age)))
			return Number(pd.age);
		if (pd.dateOfBirth) {
			const years = Math.floor((Date.now() - new Date(pd.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000));
			return isNaN(years) ? null : years;
		}
		return null;
	}, [props.patientDetails]);

	useEffect(() => {
		// The numbering system is clinic-wide. It used to be read from the
		// business-settings row keyed by the logged-in user's id, which only the
		// owning user has - everyone else silently got Universal.
		axiosInstance.get("getDentalDiagramMode")
			.then(res => {
				setIsFdi(res.data?.data?.dental_diagram_mode === "fdi");
			})
			.catch(e => {
				console.error(e)
			});
		loadPatientServicesWithTeeth();
		loadTeethNotes();
	}, []);

	const loadPatientServicesWithTeeth = async () => {
		try {
			const response = await axiosInstance.get(
				`getPatientServicesWithTeeth/${props.patientId}`
			);
			if (response.data.success === true) {
				setPatientServices(response.data.data);
			} else {
				console.error("Failed to fetch patient data:", response.data.message);
			}
		} catch (error) {
			console.error("Error fetching patient data:", error.message);
		}
	};

	const onServiceAddedSuccessfully = () => {
		loadPatientServicesWithTeeth();
	}

	const loadServiceDetailsById = async (plan) => {
		try {
			const response = await axiosInstance.get(
				`getPatientServiceById/${plan.id}`
			);
			if (response.data.success === true) {
				setSelectedServiceDetails(response.data.data);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const loadServiceDates = async () => {
		try {
			const response = await axiosInstance.get(
				`getDentalServiceDatesByPatient/${props.patientId}`
			);
			if (response.data.success === true) {
				setDentalDates(response.data.dates);
			} else {
				console.error("data did not fetch");
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		loadServiceDates();
	}, []);

	const handleServiceDateChange = (e) => {
		setCurrentDentalDate(e.target.value);
		let dateOnly = e.target.value.split("T")[0];
		filterPatientServicesByDate(dateOnly);
	};

	const filterPatientServicesByDate = async (dateSelected) => {
		if (!dateSelected) {
			loadPatientServicesWithTeeth()
			return
		}
		try {
			const response = await axiosInstance.post(
				`getDentalPatientServicesByDate/${props.patientId}`,
				{ date: dateSelected }
			);
			if (response.status === 200) {
				setPatientServices(response.data.data);
			} else {
				console.error("Something went wrong");
			}
		} catch (error) {
			console.error("Error fetching medical records:", error);
		}
	};

	const viewServiceDetails = (plan) => {
		setViewServiceModalOpen(true);
		loadServiceDetailsById(plan);
	};

	const handleEditService = (service) => {
		diagramRef.current.showEditForm(service)
	};


	const handleServiceStatusChange = async (serviceId, newStatus) => {
		try {
			const res = await axiosInstance.put(
				`updatePatientServicesStatus/${serviceId}`,
				{ status: newStatus }
			);
			if (res.data.success) {
				Swal.fire("Success", "Service status updated successfully", "success");
				dispatch(triggerLabRefresh());
				loadPatientServicesWithTeeth();
			} else {
				Swal.fire(
					"Error",
					res.data.message || "Failed to update service status",
					"error"
				);
			}
		} catch (error) {
			console.error("Service status change failed", error);
			Swal.fire("Error", "Something went wrong!", "error");
		}
	};

	const handleDeleteService = async (serviceId) => {
		const confirm = await Swal.fire({
			title: "Are you sure?",
			text: "This service will be removed from the patient!",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Yes, remove it!",
		});

		if (confirm.isConfirmed) {
			axiosInstance.delete(`deletePatientService/${serviceId}`).then((d) => {
				Swal.fire(
					"Removed!",
					"Service has been removed from patient.",
					"success"
				);
				dispatch(triggerLabRefresh());
				loadPatientServicesWithTeeth();
			}).catch(e => {
				console.error("Delete failed", e);
				Swal.fire("Error", "Something went wrong!", "error");
			})
		}
	};

	const onDiagramMouseMove = (e) => {
		const data = {
			visible: e.valid,
			text: "-"
		}
		if (e.valid) {
			data.text = `${getTeethNos([{ id: e.id }], isFdi)}: (${teethTypeDescription[e.type]}) ${e.description}`;
			data.services = [];
		}

		if (patientServices) {
			patientServices.forEach(s => {
				const teethNos = getTeethNos(s.details, false)

				let type = [e.type]
				if (e.type == "number") {
					type = ['whole_tooth', 'crow', 'extraction', 'implant', 'tooth']
				}
				if (teethNos.includes(e.id) && type.includes(s.dental_type)) {
					data.services.push(s.service_name)
				}
			})
		}
		setContextMenuData(data)
	}

	return (
		<div className="dentalCompact">
			<div ref={diagramContainerRef}>
			<DentalDiagram existingData={
				// patientServices ? patientServices.map(i => {
				// 	const d = JSON.parse(i.details);
				// 	d.forEach(t => {
				// 		t['style'] = i.dental_type;
				// 		t['status'] = i.status;
				// 	})

				// 	return d;
				// }).flatMap((cv) => cv) : []

				Array.isArray(patientServices)
					? patientServices
						.map((i) => {
							let d = [];

							try {
								d = i.details ? JSON.parse(i.details) : [];
							} catch (e) {
								console.error("Invalid JSON in details:", i.details);
								d = [];
							}

							if (!Array.isArray(d)) return [];

							d.forEach((t) => {
								t['style'] = i.dental_type;
								t['status'] = i.status;
							});

							return d;
						})
						.flat()
					: []
			}
			
			onDiagramMouseMove={onDiagramMouseMove}
			onAddedSuccessfully={onServiceAddedSuccessfully}
			onNoteAdded={loadTeethNotes}
			teethNotes={teethNotes}
			teethNotesLoading={teethNotesLoading}
			onViewTeethNote={setViewingTeethNote}
			teethTypeDescription={teethTypeDescription}
			ref={diagramRef}
			isFdi={isFdi}
			patientAge={patientAge}
			patientId={props.patientId}
			/>
			</div>
			<div style={{ fontSize: "8pt" }} className={'mt-1 context-menu alert alert-light' + (contextMenuData?.visible ? ' active' : '')}>
				{contextMenuData?.text}
				{contextMenuData?.services?.length > 0 && (contextMenuData.services.map((plan, index) => {
					return (<div key={index}>{plan}</div>)
				}))}
			</div>
			<ViewServiceModal isFdi={isFdi} setViewServiceModalOpen={setViewServiceModalOpen} viewServiceModalOpen={viewServiceModalOpen} selectedServiceDetails={selectedServiceDetails} />
			<div className="d-flex gap-3 mt-3" style={{ alignItems: "flex-start" }}>

			{/* LEFT 70% */}
			<div style={{ flex: "0 0 70%", minWidth: 0 }} className="d-flex flex-column gap-3">

			{/* Procedures */}
			<div className="card table-card">
				<div className="tableHeader bg-white d-flex align-items-center justify-content-between">
					<h5 className="mb-0">
						<i className="fas fa-tooth" style={{ color: "var(--green)" }}></i>
						Procedures
					</h5>
					<select className="form-control w-auto form-select m-0" style={{ minWidth: "200px" }} value={currentDentalDate} onChange={handleServiceDateChange}>
						<option value="">Select date</option>
						{dentalDates && dentalDates.length > 0 && dentalDates.map((item, index) => (
							<option key={index} value={item}>{moment(item, "YYYY-MM-DD").format("DD/MM/YYYY")}</option>
						))}
					</select>
				</div>
				<div className="card-body p-0"><div className="table-responsive">
					<table className="table m-0"><thead><tr>
						<th>Sn</th><th>Created</th><th>Service Name</th>
						<th style={{ width: "60px" }}>Tth#</th>
						<th style={{ minWidth: "220px" }}>Notes</th>
						<th>Amount</th><th>Discount</th><th>Total</th>
						<th style={{ width: "110px" }}>Status</th>
						<th>Created By</th><th className="text-center">Action</th>
					</tr></thead><tbody>
						{(patientServices.length > 0) ? patientServices.map((plan, index) => (
							<tr key={index}>
								<td>{index + 1}</td>
								<td style={{ whiteSpace: "nowrap" }}>{moment(plan.createdAt).format("DD/MM/YYYY")}</td>
								<td>{plan?.service_name}</td>
								<td style={{ whiteSpace: "nowrap" }}>{getTeethNos(plan?.details, isFdi).join(", ")}</td>
								<td style={{ maxWidth: "160px", whiteSpace: "pre-wrap", wordBreak: "break-word" }} dangerouslySetInnerHTML={{ __html: nl2br(plan?.extra_notes) }}></td>
								<td>{plan?.amount} BHD</td>
								<td style={{ whiteSpace: "nowrap" }}>{plan?.discount_value ? plan.discount_value + " BHD" : "-"}</td>
								<td style={{ whiteSpace: "nowrap" }}>{plan?.net_amount != null ? plan.net_amount + " BHD" : "-"}</td>
								<td>{(() => { const s = (plan.status || "").toString().toLowerCase(); const done = s === "complete" || s === "completed"; return done ? <span className="badge bg-success" style={{ fontSize: "0.78em" }}>Completed</span> : <button className="bgBtn btn-sm" style={{ fontSize: 10, padding: "5px 12px", whiteSpace: "nowrap" }} onClick={() => handleServiceStatusChange(plan.id, "Complete")}>Mark Complete</button>; })()}</td>
								<td style={{ whiteSpace: "nowrap" }}>{plan?.created_by_name ? plan.created_by_name.trim() : "-"}</td>
								<td><div className="d-flex justify-content-center">
									<div onClick={() => viewServiceDetails(plan)} style={{ cursor: "pointer" }} className="avtar avtar-xs btn-link-secondary viewIcon"><i className="ti ti-eye f-20" /><span>View</span></div>
									<div onClick={() => handleEditService(plan)} style={{ cursor: "pointer" }} className="avtar avtar-xs btn-link-secondary editIcon"><i className="ti ti-edit f-20" /><span>Edit</span></div>
									<div style={{ cursor: (plan.billing_status || "") === "Paid" ? "not-allowed" : "pointer", opacity: (plan.billing_status || "") === "Paid" ? 0.5 : 1 }} title={(plan.billing_status || "") === "Paid" ? "Cannot delete paid service" : "Delete"} className="avtar avtar-xs btn-link-secondary deleteIcon" onClick={() => (plan.billing_status || "") === "Paid" ? null : handleDeleteService(plan.id)}><i className="ti ti-trash f-20" /><span>Delete</span></div>
								</div></td>
							</tr>
						)) : (<tr><td colSpan="100%" className="text-center">N/A</td></tr>)}
					</tbody></table>
				</div></div>
			</div>

			{/* Prescriptions */}
			<div className="card table-card">
				<div className="tableHeader bg-white d-flex align-items-center justify-content-between">
					<h5 className="mb-0">
						<i className="fas fa-prescription-bottle-alt" style={{ color: "var(--green)" }}></i>
						Prescriptions
					</h5>
					<div className="addBtnIcon" style={{ cursor: "pointer" }} onClick={handlemodaldpresctiption}><i className="borderBtn ti ti-plus f-20"></i></div>
				</div>
				<div className="card-body p-0 bg-white rounded-bottom"><div className="table-responsive rounded-bottom">
					<table className="table" style={{ tableLayout: "fixed", width: "100%" }}><thead><tr>
						<th style={{ width: "40px" }}>Sn</th><th style={{ width: "95px" }}>Date</th><th style={{ width: "80px" }}>Time</th>
						<th>Medicine Name</th><th style={{ width: "130px" }}>Created By</th><th style={{ width: "140px" }}>Action</th>
					</tr></thead><tbody>
						{prescriptions && prescriptions.length > 0 ? [...prescriptions].sort((a, b) => new Date(b.prescribed_at) - new Date(a.prescribed_at)).map((plan, index) => (
							<tr key={index}>
								<td>{index + 1}</td>
								<td style={{ whiteSpace: "nowrap" }}>{new Date(plan?.prescribed_at).toLocaleDateString("en-GB")}</td>
								<td style={{ whiteSpace: "nowrap" }}>{plan?.prescribed_at ? new Date(plan.prescribed_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Bahrain" }) : ""}</td>
								<td style={{ wordBreak: "break-word" }}>{plan.medicines.map((item, i) => (<span key={i}>{item.medicine_name}{i < plan.medicines.length - 1 ? ", " : ""}</span>))}</td>
								<td style={{ whiteSpace: "nowrap" }}>{plan?.doctor_name || "-"}</td>
								<td>
									<div className="avtar avtar-xs btn-link-secondary editIcon"><i className="ti ti-printer f-20" style={{ cursor: "pointer" }} onClick={() => hadlenavigate(plan.group_id)}></i><span>View</span></div>
									<div className="avtar avtar-xs btn-link-secondary editIcon" style={{ cursor: "pointer" }} onClick={() => handleEditPrescription(plan)}><i className="ti ti-edit f-20"></i><span>Edit</span></div>
									<div className="avtar avtar-xs btn-link-secondary deleteIcon"><i className="ti ti-trash f-20" style={{ cursor: "pointer" }} onClick={() => hadlenavigatedelete(plan.group_id)}></i><span>Delete</span></div>
								</td>
							</tr>
						)) : (<tr><td colSpan="6" className="text-center">N/A</td></tr>)}
					</tbody></table>
				</div></div>
			</div>

			</div>{/* end left 70% */}

			{/* RIGHT 30% */}
			<div style={{ flex: "0 0 calc(30% - 1rem)", minWidth: 0 }} className="d-flex flex-column gap-3">

				{/* Medical Notes */}
				<div className="card table-card">
					<div className="tableHeader bg-white d-flex align-items-center justify-content-between">
						<h5 className="mb-0">
							<i className="fas fa-notes-medical" style={{ color: "var(--green)" }}></i>
							Medical Notes
						</h5>
						{onAddMedicalNote && (
							<div className="addBtnIcon" style={{ cursor: "pointer" }} onClick={onAddMedicalNote}>
								<i className="borderBtn ti ti-plus f-20" style={{ padding: "5px 12px" }}></i>
							</div>
						)}
					</div>
					<div className="card-body p-0">
						{dateSelectData && dateSelectData.length > 0 ? (
							<ul className="list-group list-group-flush">
								{dateSelectData.slice().sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate)).map((item, i) => (
									<li key={i} className="list-group-item list-group-item-action" style={{ cursor: "pointer", borderLeft: "3px solid transparent", transition: "border-color 0.15s" }}
										onMouseEnter={e => e.currentTarget.style.borderLeftColor = "#2a5478"}
										onMouseLeave={e => e.currentTarget.style.borderLeftColor = "transparent"}
										onClick={() => handleNoteClick(item)}>
										<div className="d-flex align-items-center gap-2">
											<i className="fas fa-calendar-alt" style={{ color: "#2a5478", fontSize: "11px" }}></i>
											<span style={{ fontWeight: 600, fontSize: "13px", color: "#2a5478" }}>{moment(item.appointmentDate, "YYYY-MM-DDTHH:mm:ss").format("DD/MM/YYYY")}</span>
										</div>
										{item.doctor_name && <small className="text-muted d-flex align-items-center gap-1 mt-1"><i className="fas fa-user-md" style={{ fontSize: "10px" }}></i>{item.doctor_name}</small>}
									</li>
								))}
							</ul>
						) : (
							<div className="text-center text-muted p-3" style={{ fontSize: "13px" }}>No notes available</div>
						)}
					</div>
				</div>

				{/* Allergies */}
				<div className="cardBorder">
					<div className="tableHeader">
						<div className="d-flex justify-content-between">
							<h5><i className="ti ti-alert-triangle" style={{ color: "#9E2B25" }}></i>Allergies</h5>
							{onOpenAllergy && (
								<div className="addBtnIcon" style={{ cursor: "pointer" }} onClick={onOpenAllergy}>
									<i className="borderBtn ti ti-plus f-20" style={{ padding: "5px 12px" }}></i>
								</div>
							)}
						</div>
					</div>
					<div className="card-body p-0 bg-white rounded-bottom">
						<div className="table-responsive">
							<table className="table">
								<thead>
									<tr>
										<th>SR</th>
										<th>Allergy</th>
										<th>Description</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{dataAllergy && dataAllergy.length > 0 ? (
										dataAllergy.map((plan, index) => (
											<tr key={index}>
												<td>{index + 1}</td>
												<td>{plan?.allergy_name || "N/A"}</td>
												<td>{plan?.allergy_type || "N/A"}</td>
												<td className="text-center d-flex gap-2">
													<button type="button" className="avtar avtar-xs btn-link-secondary deleteIcon" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => onDeleteAllergy && onDeleteAllergy(plan)}>
														<i className="ti ti-trash f-20" /><span>Delete</span>
													</button>
												</td>
											</tr>
										))
									) : (
										<tr><td colSpan="4" className="text-center">N/A</td></tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</div>

				{/* Chronic Illnesses */}
				<div className="cardBorder">
					<div className="tableHeader">
						<div className="d-flex justify-content-between">
							<h5><i className="ti ti-heart-rate-monitor" style={{ color: "#e67e00" }}></i>Chronic Illness</h5>
							{onOpenChronicIllness && (
								<div className="addBtnIcon" style={{ cursor: "pointer" }} onClick={onOpenChronicIllness}>
									<i className="borderBtn ti ti-plus f-20" style={{ padding: "5px 12px" }}></i>
								</div>
							)}
						</div>
					</div>
					<div className="card-body p-0 bg-white rounded-bottom">
						<div className="table-responsive rounded-bottom">
							<table className="table">
								<thead>
									<tr>
										<th>SR</th>
										<th>Illness</th>
										<th>Action</th>
									</tr>
								</thead>
								<tbody>
									{chronicillness1 && chronicillness1.length > 0 ? (
										chronicillness1.map((plan, index) => (
											<tr key={index}>
												<td>{index + 1}</td>
												<td>{plan?.illness_text || "N/A"}</td>
												<td className="text-center d-flex gap-2">
													<button type="button" className="avtar avtar-xs btn-link-secondary editIcon" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => onEditChronicIllness && onEditChronicIllness(plan)}>
														<i className="ti ti-edit f-20" /><span>Edit</span>
													</button>
													<button type="button" className="avtar avtar-xs btn-link-secondary deleteIcon" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => onDeleteChronicIllness && onDeleteChronicIllness(plan)}>
														<i className="ti ti-trash f-20" /><span>Delete</span>
													</button>
												</td>
											</tr>
										))
									) : (
										<tr><td colSpan="3" className="text-center">N/A</td></tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</div>

				{/* Actions */}
				<div className="card table-card">
					<div className="tableHeader bg-white d-flex align-items-center">
						<h5 className="mb-0">
							<i className="fas fa-bolt" style={{ color: "#2ca87f" }}></i>
							Actions
						</h5>
					</div>
					<div className="card-body bg-white rounded-bottom d-flex flex-column gap-2 p-3">
						<button className="bgBtn btn-sm w-100 d-flex align-items-center justify-content-center gap-2" style={{ fontSize: 12.5, padding: "8px 14px" }} onClick={handlePrintPlan}><i className="ti ti-printer"></i>Print Plan</button>
					</div>
				</div>

			</div>{/* end right 30% */}
			</div>{/* end unified flex row */}


		{/* ── Medical Notes Modal ── */}
		{notesModalOpen && (
			<div className="modal fade show" style={{ backgroundColor: "rgba(81,81,81,0.5)", display: "block" }}>
				<div className="modal-dialog modal-lg">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title">
								Medical Notes — {selectedNoteDate?.date && moment(selectedNoteDate.date, "YYYY-MM-DD").format("DD/MM/YYYY")}
								{selectedNoteDate?.doctor_name && <small className="ms-2 text-muted" style={{ fontSize: "14px" }}>{selectedNoteDate.doctor_name}</small>}
							</h5>
							<button type="button" className="btn-close" onClick={() => setNotesModalOpen(false)} />
						</div>
						<div className="modal-body">
							{notesLoading ? (
								<div className="text-center p-4">Loading...</div>
							) : selectedNoteData ? (
								<div className="row g-3">
									{selectedNoteData.chief_complaint && (
										<div className="col-12">
											<label className="fw-semibold">Chief Complaint</label>
											<p className="mb-0">{selectedNoteData.chief_complaint}</p>
										</div>
									)}
									{selectedNoteData.history_of_present_illness && (
										<div className="col-12">
											<label className="fw-semibold">History of Present Illness</label>
											<p className="mb-0">{selectedNoteData.history_of_present_illness}</p>
										</div>
									)}
									{selectedNoteData.history_of_past_illness && (
										<div className="col-12">
											<label className="fw-semibold">Past Illness</label>
											<p className="mb-0">{selectedNoteData.history_of_past_illness}</p>
										</div>
									)}
									{selectedNoteData.examination_general && (
										<div className="col-12">
											<label className="fw-semibold">General Examination</label>
											<p className="mb-0">{selectedNoteData.examination_general}</p>
										</div>
									)}
									{selectedNoteData.examination_systemic && (
										<div className="col-12">
											<label className="fw-semibold">Systemic Examination</label>
											<p className="mb-0">{selectedNoteData.examination_systemic}</p>
										</div>
									)}
									{selectedNoteData.examination_local && (
										<div className="col-12">
											<label className="fw-semibold">Local Examination</label>
											<p className="mb-0">{selectedNoteData.examination_local}</p>
										</div>
									)}
									{selectedNoteData.family_history && (
										<div className="col-12">
											<label className="fw-semibold">Family History</label>
											<p className="mb-0">{selectedNoteData.family_history}</p>
										</div>
									)}
									{selectedNoteData.psychosocial && (
										<div className="col-12">
											<label className="fw-semibold">Psychosocial</label>
											<p className="mb-0">{selectedNoteData.psychosocial}</p>
										</div>
									)}
									{!selectedNoteData.chief_complaint && !selectedNoteData.history_of_present_illness && !selectedNoteData.examination_general && (
										<div className="col-12 text-muted text-center">No notes recorded for this date.</div>
									)}
								</div>
							) : (
								<div className="text-center text-muted">No data found.</div>
							)}
						</div>
						<div className="modal-footer">
							<button type="button" className="cancelText" onClick={() => setNotesModalOpen(false)}>Close</button>
						</div>
					</div>
				</div>
			</div>
		)}


		{/* ── Teeth Note Detail Modal ── */}
		{viewingTeethNote && (() => {
			let parsedTeeth = [];
			try { parsedTeeth = JSON.parse(viewingTeethNote.teeth_ids || '[]'); } catch (e) {}
			const teethNos = getTeethNos(parsedTeeth, isFdi);
			const locked = isRecordLocked(viewingTeethNote.created_at);
			const closeModal = () => { setViewingTeethNote(null); setIsEditingTeethNote(false); };
			return (
				<div className="modal fade show" style={{ backgroundColor: "rgba(20,40,60,0.55)", display: "block" }} onClick={closeModal}>
					<div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
						<div className="modal-content" style={{ borderRadius: 10, overflow: "hidden", border: "none", boxShadow: "0 8px 32px rgba(42,84,120,0.18)" }}>
							<div className="modal-header" style={{ background: "linear-gradient(135deg,#f0f5fa 0%,#f8fafc 100%)", borderBottom: "1px solid rgba(219,224,229,0.8)", padding: "12px 16px" }}>
								<h5 className="modal-title" style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--green)", display: "flex", alignItems: "center", gap: 6, marginBottom: 0 }}>
									<i className="fas fa-tooth" style={{ fontSize: 15 }}></i>
									Teeth Note{teethNos.length > 0 && ` — ${teethNos.join(", ")}`}
								</h5>
								<button type="button" onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 18, lineHeight: 1, padding: "0 2px" }}>
									<i className="ti ti-x"></i>
								</button>
							</div>
							<div className="d-flex align-items-center gap-3" style={{ fontSize: "12px", color: "#6b7280", padding: "8px 16px 0" }}>
								<span><i className="fas fa-user-md me-1"></i>{viewingTeethNote.doctor_name?.trim() || "Unknown"}</span>
								<span><i className="fas fa-clock me-1"></i>{moment(viewingTeethNote.created_at).format("DD/MM/YYYY  HH:mm")}</span>
								{locked && (
									<span><i className="fas fa-lock me-1"></i>Locked</span>
								)}
							</div>
							<div className="modal-body" style={{ padding: "20px" }}>
								{isEditingTeethNote ? (
									<textarea
										className="form-control"
										value={editTeethNoteText}
										onChange={(e) => setEditTeethNoteText(e.target.value)}
										autoFocus
										style={{ minHeight: "160px", maxHeight: "50vh", fontSize: "14px", lineHeight: 1.6, resize: "vertical" }}
									/>
								) : (
									<div style={{ background: "#f4f8fb", border: "1px solid #d0e4f0", borderLeft: "4px solid #2a5478", borderRadius: "8px", padding: "14px 16px", whiteSpace: "pre-wrap", fontSize: "14px", color: "#2c3e50", lineHeight: 1.6, minHeight: "80px", maxHeight: "50vh", overflowY: "auto" }}>
										{viewingTeethNote.note_text}
									</div>
								)}
							</div>
							<div className="modal-footer" style={{ borderTop: "1px solid #e8f0f7", padding: "10px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
								{isEditingTeethNote ? (
									<>
										<button type="button" className="cancelText" onClick={handleCancelEditTeethNote} disabled={teethNoteSaving}>Cancel</button>
										<button type="button" className="bgBtn btn-sm" style={{ padding: "6px 20px" }} onClick={handleSaveTeethNote} disabled={teethNoteSaving || !editTeethNoteText.trim()}>
											{teethNoteSaving ? "Saving..." : "Save"}
										</button>
									</>
								) : (
									<>
										<div className="d-flex gap-2">
											<button
												type="button"
												className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
												style={{ padding: "6px 14px", cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1 }}
												onClick={() => !locked && handleStartEditTeethNote()}
												disabled={locked}
												title={locked ? "Record is locked" : "Edit note"}
											>
												<i className="ti ti-edit"></i> Edit
											</button>
											<button
												type="button"
												className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
												style={{ padding: "6px 14px", cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1 }}
												onClick={() => !locked && handleDeleteTeethNote()}
												disabled={locked}
												title={locked ? "Record is locked" : "Delete note"}
											>
												<i className="ti ti-trash"></i> Delete
											</button>
										</div>
										<button type="button" className="bgBtn btn-sm" style={{ padding: "6px 20px" }} onClick={closeModal}>Close</button>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			);
		})()}

		{/* ── Prescription Modal ── */}
		{prescriptionModalOpen && (
			<div className="modal fade show" style={{ backgroundColor: "rgba(81,81,81,0.5)", display: "block" }}>
				<div className="modal-dialog modal-xl" style={{ maxWidth: "1200px" }}>
					<div className="modal-content">
						<div className="modal-header py-2">
							<h6 className="modal-title mb-0">
								{isEditMode ? "Edit Prescription" : "Add Prescription"}
							</h6>
							<button type="button" className="btn-close" onClick={() => { setPrescriptionModalOpen(false); setIsEditMode(false); setEditingGroupId(null); }} />
						</div>
						<div className="modal-body py-2 px-3">
							<div className="row g-2">
								<div className="col-auto" style={{ width: "160px" }}>
									<MedicalTemplateList onSelect={handleSelectPerscriptionTemplate} templateType="prescriptions" />
								</div>
								<div className="col">
									<div className="row g-2 mb-1">
										<div className="col-5">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Medicine (from List)</label>
											<Select
												isSearchable
												name="rxId"
												options={rxList.map((item) => ({ value: item.rxId, label: item.medicineName, strength: item.strength, unit: item.unit, activeSubstances: item.activeSubstances }))}
												value={rxList.find((item) => item.rxId === medicineData?.rxId) ? { value: medicineData.rxId, label: rxList.find((item) => item.rxId === medicineData.rxId).medicineName } : null}
												onChange={handleEditLabChange123}
												classNamePrefix="select"
												placeholder="Select Medicine"
												styles={{ control: (b) => ({ ...b, fontSize: "13px" }), option: (b) => ({ ...b, fontSize: "13px" }) }}
												formatOptionLabel={(data) => (
													<div style={{ display: "flex", flexDirection: "column" }}>
														<strong style={{ fontSize: "12px", color: "#333" }}>{data.label}</strong>
														<small style={{ color: "#666", fontSize: "10px" }}>{data.strength && `${data.strength} | `}{data.unit}</small>
													</div>
												)}
												filterOption={(option, inputValue) => {
													const s = inputValue.toLowerCase();
													return option.data.label.toLowerCase().includes(s) ||
														(option.data.strength && option.data.strength.toLowerCase().includes(s)) ||
														(option.data.activeSubstances && option.data.activeSubstances.toLowerCase().includes(s));
												}}
											/>
										</div>
										<div className="col-4">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Medicine Name</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="medicine_name" value={medicineData.medicine_name} onChange={handleMedicineChange} placeholder="Medicine name" />
										</div>
										<div className="col-3">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Active Substances</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="substance" value={medicineData.substance} onChange={handleMedicineChange} placeholder="Substance" />
										</div>
									</div>
									<div className="row g-2 mb-1">
										<div className="col-3">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Dose</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="dose" value={medicineData.dose} onChange={handleMedicineChange} placeholder="Dose" />
										</div>
										<div className="col-3">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Frequency</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="frequency" value={medicineData.frequency} onChange={handleMedicineChange} placeholder="Frequency" />
										</div>
										<div className="col-3">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Duration</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="duration" value={medicineData.duration} onChange={handleMedicineChange} placeholder="Duration" />
										</div>
										<div className="col-3">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Form</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="form" value={medicineData.form} onChange={handleMedicineChange} placeholder="Form" />
										</div>
									</div>
									<div className="row g-2 mb-2">
										<div className="col-2">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Strength</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="strength" value={medicineData.strength} onChange={handleMedicineChange} placeholder="Strength" />
										</div>
										<div className="col-2">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Unit</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="unit" value={medicineData.unit} onChange={handleMedicineChange} placeholder="Unit" />
										</div>
										<div className="col-2">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Route</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="route" value={medicineData.route} onChange={handleMedicineChange} placeholder="Route" />
										</div>
										<div className="col-2">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Pack Size</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="packet_size" value={medicineData.packet_size} onChange={handleMedicineChange} placeholder="Pack size" />
										</div>
										<div className="col-2">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Product Type</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="productType" value={medicineData.productType} onChange={handleMedicineChange} placeholder="Type" />
										</div>
										<div className="col-2">
											<label className="form-label mb-0" style={{ fontSize: "11px" }}>Notes</label>
											<input className="form-control" style={{ height: "38px", fontSize: "13px" }} name="notes" value={medicineData.notes} onChange={handleMedicineChange} placeholder="Notes" />
										</div>
									</div>
									<div className="text-end mb-2">
										<button type="button" className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: "5px 12px" }} onClick={handleAddMedicine}>
											{editIndex !== null ? "Update Medicine" : "+ Add Medicine"}
										</button>
									</div>
									<div style={{ maxHeight: "180px", overflowY: "auto", border: "1px solid #e9ecef", borderRadius: "6px" }}>
										<table className="table table-sm mb-0" style={{ fontSize: "12px" }}>
											<thead style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 1 }}>
												<tr>
													<th style={{ padding: "5px 8px" }}>#</th>
													<th style={{ padding: "5px 8px" }}>Medicine</th>
													<th style={{ padding: "5px 8px" }}>Dose</th>
													<th style={{ padding: "5px 8px" }}>Frequency</th>
													<th style={{ padding: "5px 8px" }}>Duration</th>
													<th style={{ padding: "5px 8px", textAlign: "center" }}>Action</th>
												</tr>
											</thead>
											<tbody>
												{addedMedicines.length > 0 ? addedMedicines.map((item, index) => (
													<tr key={index}>
														<td style={{ padding: "4px 8px" }}>{index + 1}</td>
														<td style={{ padding: "4px 8px" }}>{item.medicine_name}</td>
														<td style={{ padding: "4px 8px" }}>{item.dose}</td>
														<td style={{ padding: "4px 8px" }}>{item.frequency}</td>
														<td style={{ padding: "4px 8px" }}>{item.duration}</td>
														<td style={{ padding: "4px 8px", textAlign: "center" }}>
															<button className="bgBtn btn-sm" style={{ fontSize: 13, padding: "5px 12px", marginRight: "4px" }} onClick={() => handleEditMedicine(index)}>Edit</button>
															<button className="bgBtn btn-sm" style={{ fontSize: 13, padding: "5px 12px" }} onClick={() => handleDeleteMedicine(index)}>Delete</button>
														</td>
													</tr>
												)) : (
													<tr><td colSpan="6" className="text-center text-muted" style={{ padding: "10px" }}>No medicines added yet</td></tr>
												)}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer py-2 justify-content-end">
							<button type="button" className="cancelText" onClick={() => { setPrescriptionModalOpen(false); setIsEditMode(false); setEditingGroupId(null); }}>Cancel</button>
							<button type="button" className="bgBtn btn-sm" onClick={handlesubmitprescription}>
								{isEditMode ? "Update Prescription" : "Submit Prescription"}
							</button>
						</div>
					</div>
				</div>
			</div>
		)}

		</div>
	);
}